import {NativeAppleCalendarActionModal} from './NativeAppleCalendarActionModal';
import {appleCalendarJournal} from '@/lib/appleCalendarWriteJournal';
import type {AppleMirrorReceiptReference} from '@/lib/appleMirrorReceipt';
import { useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import ConnectionManageSurface from "@/components/connections/ConnectionManageSurface";
import { CalendarActionModal } from "@/components/mirror/CalendarActionModal";
import { LocalAgendaActionModal } from "@/components/mirror/LocalAgendaActionModal";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { useLanguage } from "@/i18n/LanguageProvider";
import { MIRROR_AGENDA_CARDS_ENABLED } from "@/lib/featureFlags";
import { loadConnectorWrite, type PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import type { CalendarActionPrefill, CalendarProvider, MirrorCalendarReceiptReference } from "@/lib/calendarAction";
import type { LocalAgendaReceiptReference } from "@/lib/agendaExecutor";

interface Props {
  open: boolean;
  prefill: CalendarActionPrefill | null;
  onClose: () => void;
  onCreated: (receipt?: MirrorCalendarReceiptReference | LocalAgendaReceiptReference | AppleMirrorReceiptReference) => void | Promise<boolean>;
}
type Destination = "vyv" | CalendarProvider | "apple_calendar";
const sameOrigin = (entry: PendingConnectorWrite | null, prefill: CalendarActionPrefill) => {
  const origin = entry?.body.mirror_context as { session_id?: unknown; action_id?: unknown } | undefined;
  return entry?.ownerId === prefill.expected_user_id && origin?.session_id === prefill.expected_session_id && origin?.action_id === prefill.action_id;
};

export function CalendarCreationDestinationModal(props: Props) {
  const identity = useLibraryIdentity();
  if (!props.open || !props.prefill || !identity.ready || !identity.userId ||
    props.prefill.expected_user_id !== identity.userId || !props.prefill.expected_session_id) return null;
  if (props.prefill.action !== "create") return <CalendarActionModal {...props} />;
  return <CreationDestination key={[identity.userId, identity.generation, props.prefill.expected_session_id,
    props.prefill.action_id].join(":")} {...props} prefill={props.prefill} ownerId={identity.userId} />;
}

function CreationDestination({ prefill, ownerId, onClose, onCreated }: Props & { prefill: CalendarActionPrefill; ownerId: string }) {
  const { t } = useLanguage();
  const [destination, setDestination] = useState<Destination | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const lifecycle = useRef(0);
  useEffect(() => {
    const run = ++lifecycle.current;
    // An uncertain or completed write pins its destination. Recovery never
    // offers a second writer for the same proposal and never dispatches itself.
    void Promise.all([loadConnectorWrite(ownerId, "local_agenda"), loadConnectorWrite(ownerId, "calendar"),appleCalendarJournal.load(ownerId)]).then(([local, external,apple]) => {
      if (run !== lifecycle.current) return;
      const localMatches = sameOrigin(local, prefill), externalMatches = sameOrigin(external, prefill);
      const appleMatches=apple?.owner_id===prefill.expected_user_id&&apple.origin?.session_id===prefill.expected_session_id&&apple.origin?.action_id===prefill.action_id;
      if ([localMatches,externalMatches,appleMatches].filter(Boolean).length>1) { setError(true); return; }
      if (localMatches) {
        if (!prefill.vyv_draft || local!.body.action !== "create" || local!.body.operation_id !== prefill.vyv_draft.operation_id ||
          local!.body.event_id !== prefill.vyv_draft.event_id) { setError(true); return; }
        setDestination("vyv");
      } else if (externalMatches) {
        const provider = external!.body.provider;
        if ((provider !== "google_calendar" && provider !== "outlook") || external!.body.action !== "create_event" ||
          external!.body.idempotency_key !== "mirror_calendar_" + prefill.action_id + "_create_event_0") { setError(true); return; }
        setDestination(provider);
      } else if(appleMatches){if(apple!.review.payload.action!=='create_event'){setError(true);return;}setDestination('apple_calendar');}
      setReady(true);
    }).catch(() => { if (run === lifecycle.current) setError(true); });
    const life = lifecycle;
    return () => { life.current++; };
  }, [ownerId, prefill]);

  if (destination === "vyv" && prefill.vyv_draft) return <LocalAgendaActionModal open prefill={prefill.vyv_draft} onClose={onClose} onCreated={onCreated} />;
  if(destination==='apple_calendar')return <NativeAppleCalendarActionModal open prefill={{...prefill,provider:'apple_calendar',vyv_draft:undefined}} onClose={onClose} onCreated={onCreated}/>;
  if (destination && destination !== "vyv") return <CalendarActionModal open prefill={{ ...prefill, provider: destination }} onClose={onClose} onCreated={onCreated} />;
  return <ConnectionManageSurface open onOpenChange={open => { if (!open) onClose(); }}
    title={t("mirror.calendar.destination.title")} description={t("mirror.calendar.destination.description")}
    closeLabel={t("common.close")} brand={<CalendarDays className="h-6 w-6" />}>
    <div className="min-w-0 space-y-3">
      {prefill.title && <p className="break-words text-sm font-semibold">{prefill.title}</p>}
      {error ? <p role="alert" className="text-sm text-destructive">{t("mirror.connector.storageUnavailable")}</p>
        : !ready ? <p role="status" className="text-sm text-muted-foreground">{t("common.loading")}</p>
        : <>
          <button type="button" disabled={!prefill.vyv_draft || !MIRROR_AGENDA_CARDS_ENABLED} onClick={() => setDestination("vyv")}
            className="min-h-12 w-full rounded-xl border border-primary/50 bg-primary/10 px-4 py-3 text-left">
            <span className="block text-sm font-semibold text-primary">{t("mirror.calendar.destination.vyv")}</span>
            <span className="mt-1 block text-xs text-muted-foreground">{t("mirror.calendar.destination.vyvDescription")}</span>
          </button>
          <p className="pt-1 text-sm font-medium">{t("mirror.calendar.destination.external")}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(["google_calendar", "outlook", "apple_calendar"] as const).map(provider => <button key={provider} type="button"
              onClick={() => setDestination(provider)} className="min-h-12 rounded-xl border border-border px-3 py-3 text-sm font-semibold">
              {provider === "google_calendar" ? "Google Calendar" : provider==='outlook'?"Microsoft Outlook":'Apple Calendar'}
            </button>)}
          </div>
          <p className="text-xs text-muted-foreground">{t("mirror.calendar.destination.reviewFirst")}</p>
        </>}
    </div>
  </ConnectionManageSurface>;
}
